import * as React from "react"
import {Divider, Form, Header, Loader, Segment, Table} from "semantic-ui-react"
import {
  DriverFragment,
  Drivers as DRIVERS,
  Drivers as DriversQuery,
  UpsertDriver as UPSERT_DRIVER,
  UpsertDriverMutationVariables,
} from "../graphql/queries"
import {GraphQL, makeQueryWithVarsNoT} from "../graphql"
import {BookerPicker} from "./BookerPicker"
import {Booker} from "../model/BookingTypes"
import {ID} from "../model/id"
import {formatMobile, isValidPhoneNumber, PhoneField} from "./PhoneField"
import {errorUnionToEither, handleError, OrError} from "../graphql/errors"
import {WithModalStore} from "../App"
import {ModalStore} from "../actions"

const isInsert = (id: ID | null) => id === null

export interface DriverData {
  id: ID | null
  booker: Booker | null
  name: string
  mobileNumber: string
  active: boolean
}

function emptyDriverData(): DriverData {
  return {
    id: null,
    booker: null,
    name: "",
    mobileNumber: "",
    active: true,
  }
}

function DriverForm({
  driverData: whole,
  driverData: {id, booker, name, mobileNumber, active},
  onDriver,
  onReset,
  onSave,
  modalStore,
}: {
  modalStore: ModalStore
  driverData: DriverData
  onReset: () => void
  onDriver: (x: DriverData) => void
  onSave: () => Promise<void>
}) {
  const countryCode = "AU"

  // TODO The first condition here is a proxy for "not the initial empty state".
  const nameError = mobileNumber.length > 0 && name === ""
  const mobileError = mobileNumber.length > 0 && !isValidPhoneNumber(mobileNumber)
  const saveDisabled = name === "" || mobileNumber === "" || mobileError

  return (
    <Segment.Group>
      <Segment inverted color="olive">
        <Form>
          <Header>{id === null ? "New Driver" : "Edit driver"}</Header>
          <Form.Field required error={nameError}>
            <label>Name</label>
            <Form.Input
              value={name}
              placeholder="Name"
              onChange={(e, x) => {
                onDriver({...whole, name: x.value})
              }}
            />
          </Form.Field>
          <Form.Field>
            <label>Booker</label>
            <BookerPicker
              onBooker={(x: Booker | null) => {
                onDriver({...whole, booker: x})
              }}
              booker={booker}
              disabled={false}
              modalStore={modalStore}
              bookerFormOpen={false}
              onBookerFormOpen={null /* TODO Allow admin to create a booker for the driver? */}
            />
          </Form.Field>
          <PhoneField
            label="Mobile number"
            phoneNumber={mobileNumber}
            required={true}
            error={mobileError}
            onChange={(x: string) => {
              onDriver({...whole, mobileNumber: x})
            }}
          />
          <Form.Field inline>
            <label>Active</label>
            <Form.Checkbox
              checked={active}
              onChange={(e, x) => {
                onDriver({...whole, active: x.checked || false})
              }}
            />
          </Form.Field>
          <Divider />
          <Form.Group>
            <Form.Button
              positive
              disabled={saveDisabled}
              children={"Save"}
              onClick={(e) => {
                e.preventDefault()
                modalStore.handlePromiseThrow(onSave())
              }}
            />
            <Form.Button
              children={"Reset"}
              onClick={(e) => {
                e.preventDefault()
                onReset()
              }}
            />
          </Form.Group>
        </Form>
      </Segment>
    </Segment.Group>
  )
}

function DriverRow({
  driver: {id, name, booker, mobileNumber, active},
  onSelectDriver,
}: {
  driver: DriverFragment
  onSelectDriver: () => void
}) {
  return (
    <Table.Row onClick={onSelectDriver}>
      <Table.Cell singleLine>{name}</Table.Cell>
      <Table.Cell singleLine>{booker ? booker.name : ""}</Table.Cell>
      <Table.Cell singleLine>{mobileNumber}</Table.Cell>
      <Table.Cell singleLine>{active ? "Yes" : "No"}</Table.Cell>
    </Table.Row>
  )
}

function DriversList({
  drivers,
  onDriver,
}: {
  drivers: Array<DriverFragment>
  onDriver: (x: DriverData) => void
}) {
  if (drivers.length === 0) {
    return <div>No drivers found</div>
  }

  return (
    <Table celled selectable compact="very">
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>Driver name</Table.HeaderCell>
          <Table.HeaderCell>Booker name</Table.HeaderCell>
          <Table.HeaderCell>Mobile</Table.HeaderCell>
          <Table.HeaderCell>Active?</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {drivers.map((driver) => (
          <DriverRow key={driver.id} driver={driver} onSelectDriver={() => onDriver({...driver})} />
        ))}
      </Table.Body>
    </Table>
  )
}

class Drivers extends React.Component<Props, {driverData: DriverData}> {
  constructor(props: Props) {
    super(props)
    this.state = {driverData: emptyDriverData()}
  }

  handleReset = () => this.setState({driverData: emptyDriverData()})

  handleDriver = (driverData: DriverData) => this.setState({driverData})

  render() {
    const {driverData} = this.state
    const {drivers, modalStore} = this.props

    return (
      <div>
        <Header>Drivers</Header>
        <DriversList drivers={drivers} onDriver={this.handleDriver} />
        <DriverForm
          driverData={driverData}
          onDriver={this.handleDriver}
          onReset={this.handleReset}
          onSave={async () => {
            await this.onSave(driverData)
            this.handleReset()
          }}
          modalStore={modalStore}
        />
      </div>
    )
  }

  onSave(driverData: DriverData): Promise<OrError<void>> {
    const {modalStore} = this.props
    const id = driverData.id
    if (isInsert(id)) {
      return modalStore.applyModal({
        pending: ["Creating driver", "Creating a new driver..."],
        error: (m) => ["Saving driver failed", m],
        action: () => onUpsert(driverData),
      })
    } else {
      return modalStore.applyModal({
        pending: ["Updating driver", "Updating this driver..."],
        error: (m) => ["Updating driver failed", m],
        action: () => onUpsert(driverData),
      })
    }
  }
}

async function onUpsert(driverData: DriverData): Promise<OrError<void>> {
  const {id, booker, name, mobileNumber, active} = driverData

  const variables: UpsertDriverMutationVariables = {
    id,
    driver: {
      bookerId: booker ? booker.id : null,
      name,
      mobileNumber: formatMobile(mobileNumber),
      active,
    },
  }

  // Refetch drivers after an insert so the drivers list can show the new driver
  const refetchQueries = isInsert(id)
    ? [
        makeQueryWithVarsNoT({
          query: DRIVERS,
          variables: {},
        }),
      ]
    : []

  const data = await GraphQL.mutate(UPSERT_DRIVER, variables, {refetchQueries})
  return errorUnionToEither(data.upsertDriver).map(() => undefined)
}

interface Props extends WithModalStore {
  drivers: Array<DriverFragment>
}

function WithDrivers(props: WithModalStore) {
  return (
    <GraphQL.Query
      query={DriversQuery}
      variables={{}}
      render={({data}) => {
        if (data) {
          return handleError(errorUnionToEither(data.drivers), (drivers) => (
            <Drivers {...props} drivers={drivers.drivers} />
          ))
        } else {
          return <Loader active />
        }
      }}
    />
  )
}

export {WithDrivers as DriversAdmin}



// WEBPACK FOOTER //
// ./src/components/DriversAdmin.tsx